/*
  Component: Cookie Preferences panel (Your Privacy Choices page)
  Responsibility: stage the three toggleable categories in memory, drive the single-row
                  accordion, and only touch localStorage / Shopify's Customer Privacy API
                  when Save is pressed.

  Ported from a Hydrogen page's useState-based component. The asymmetry in the mapping
  functions below is intentional and carried over unchanged: "targeting" writes to BOTH
  consent.marketing and consent.sale_of_data on save, but only reads back true when BOTH
  are true. Do not simplify that to a single field — see categoryStateToConsent /
  consentToCategoryState.

  This panel does not read from or write to the Shopify-hosted opt-out widget elsewhere on
  the page. The two are separate consent surfaces by design.
*/

const PRIVACY_CONSENT_STORAGE_KEY = 'kl-cookie-preferences-v1';
const PRIVACY_TOGGLE_ON_CLASS = 'is-on';

const PRIVACY_DEFAULT_CATEGORIES = {
  functional: true,
  targeting: true,
  performance: true,
};

/* Pure: consent (as sent to/from Shopify) -> the three toggleable UI categories. */
function consentToCategoryState(consent) {
  return {
    functional: consent.preferences,
    targeting: consent.marketing && consent.sale_of_data,
    performance: consent.analytics,
  };
}

/* Pure: the three toggleable UI categories -> consent payload for Shopify. */
function categoryStateToConsent(categories) {
  return {
    marketing: categories.targeting,
    analytics: categories.performance,
    preferences: categories.functional,
    sale_of_data: categories.targeting,
  };
}

/* Pure: is a parsed localStorage value a well-formed consent object. */
function getIsValidStoredConsent(parsed) {
  return (
    parsed != null &&
    typeof parsed.marketing === 'boolean' &&
    typeof parsed.analytics === 'boolean' &&
    typeof parsed.preferences === 'boolean' &&
    typeof parsed.sale_of_data === 'boolean'
  );
}

function readStoredConsent() {
  try {
    const raw = window.localStorage.getItem(PRIVACY_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!getIsValidStoredConsent(parsed)) return null;

    return {
      marketing: parsed.marketing,
      analytics: parsed.analytics,
      preferences: parsed.preferences,
      sale_of_data: parsed.sale_of_data,
    };
  } catch (error) {
    console.debug('kl-privacy-choices: could not read stored consent', { error });
    return null;
  }
}

function writeStoredConsent(consent) {
  try {
    window.localStorage.setItem(PRIVACY_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch (error) {
    console.debug('kl-privacy-choices: could not write stored consent', { error });
  }
}

function getPrivacyElements(rootElement) {
  return {
    toggleButtons: Array.from(rootElement.querySelectorAll('[data-kl-privacy-toggle]')),
    expandButtons: Array.from(rootElement.querySelectorAll('[data-kl-privacy-expand]')),
    rejectButton: rootElement.querySelector('[data-kl-privacy-reject]'),
    acceptButton: rootElement.querySelector('[data-kl-privacy-accept]'),
    saveButton: rootElement.querySelector('[data-kl-privacy-save]'),
    statusElement: rootElement.querySelector('[data-kl-privacy-status]'),
    savedText: rootElement.querySelector('[data-kl-privacy-i18n-saved]')?.textContent || '',
    errorText: rootElement.querySelector('[data-kl-privacy-i18n-error]')?.textContent || '',
    expandText: rootElement.querySelector('[data-kl-privacy-i18n-expand]')?.textContent || '',
    collapseText: rootElement.querySelector('[data-kl-privacy-i18n-collapse]')?.textContent || '',
  };
}

function renderToggles(elements, categories) {
  elements.toggleButtons.forEach((button) => {
    const key = button.dataset.klPrivacyToggle;
    const isOn = Boolean(categories[key]);

    button.classList.toggle(PRIVACY_TOGGLE_ON_CLASS, isOn);
    button.setAttribute('aria-pressed', isOn ? 'true' : 'false');
  });
}

function renderExpandButton(rootElement, elements, button, isExpanded) {
  const key = button.dataset.klPrivacyExpand;
  const panel = rootElement.querySelector(`[data-kl-privacy-panel="${key}"]`);

  if (!panel) return;

  panel.hidden = !isExpanded;
  button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  button.setAttribute('aria-label', isExpanded ? elements.collapseText : elements.expandText);

  const glyph = button.querySelector('[aria-hidden]');
  if (glyph) glyph.textContent = isExpanded ? '-' : '+';
}

function clearStatus(elements) {
  if (!elements.statusElement) return;

  elements.statusElement.hidden = true;
  elements.statusElement.textContent = '';
}

function showStatus(elements, text) {
  if (!elements.statusElement) return;

  elements.statusElement.textContent = text;
  elements.statusElement.hidden = false;
}

function applyConsent(elements, categories) {
  const consent = categoryStateToConsent(categories);
  writeStoredConsent(consent);

  if (!window.Shopify?.customerPrivacy?.setTrackingConsent) {
    showStatus(elements, elements.savedText);
    return;
  }

  try {
    window.Shopify.customerPrivacy.setTrackingConsent(consent, (result) => {
      if (result && result.error) {
        console.debug('kl-privacy-choices: consent update error', { result });
        showStatus(elements, elements.errorText);
        return;
      }

      showStatus(elements, elements.savedText);
    });
  } catch (error) {
    console.debug('kl-privacy-choices: could not call setTrackingConsent', { error });
    showStatus(elements, elements.errorText);
  }
}

function initPrivacyChoices(rootElement) {
  const elements = getPrivacyElements(rootElement);

  const stored = readStoredConsent();
  let categories = stored ? consentToCategoryState(stored) : { ...PRIVACY_DEFAULT_CATEGORIES };
  let expandedKey = null;

  renderToggles(elements, categories);

  elements.toggleButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const key = button.dataset.klPrivacyToggle;
      if (!key) return;

      categories = { ...categories, [key]: !categories[key] };
      renderToggles(elements, categories);
      clearStatus(elements);
    });
  });

  elements.expandButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const key = button.dataset.klPrivacyExpand;
      if (!key) return;

      const previousKey = expandedKey;
      expandedKey = expandedKey === key ? null : key;

      if (previousKey && previousKey !== expandedKey) {
        const previousButton = elements.expandButtons.find(
          (candidate) => candidate.dataset.klPrivacyExpand === previousKey
        );
        if (previousButton) renderExpandButton(rootElement, elements, previousButton, false);
      }

      renderExpandButton(rootElement, elements, button, expandedKey === key);
    });
  });

  if (elements.acceptButton) {
    elements.acceptButton.addEventListener('click', () => {
      categories = { functional: true, targeting: true, performance: true };
      renderToggles(elements, categories);
      clearStatus(elements);
    });
  }

  if (elements.rejectButton) {
    elements.rejectButton.addEventListener('click', () => {
      categories = { functional: false, targeting: false, performance: false };
      renderToggles(elements, categories);
      clearStatus(elements);
    });
  }

  if (elements.saveButton) {
    elements.saveButton.addEventListener('click', () => {
      clearStatus(elements);
      applyConsent(elements, categories);
    });
  }
}

document.querySelectorAll('[data-kl-privacy-choices]').forEach(initPrivacyChoices);
